import { CategoryButton, ViewStoreButton } from '@/components/feature/floor-guide';
import { PaperIcon } from '@/components/icons/floor-guide';
import type { BuildingCategory } from '@/services/dto';
import { useConfigStore } from '@/stores';
import { getCategoryIcon } from '@/utils/floor-guide';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';
import { useAccessibilityTTS } from '@/hooks';
import { NAV_KEYS } from '@/constants/keyboard';

type Props = {
    buildingCategories?: BuildingCategory[];
    onShowPoiList?: () => void;
    onSelectCategory?: (category: BuildingCategory) => void;
    isShowCategoryList?: boolean;
};

export const VerticalCategoryList = ({ buildingCategories, onShowPoiList, onSelectCategory, isShowCategoryList }: Props) => {
    const { t, i18n } = useTranslation();
    const screenMode = useConfigStore((state) => state.config?.ui?.screenMode);
    const isFullscreen = screenMode === 'fullscreen';
    const language = useMemo(() => i18n.language, [i18n]);

    const { speak } = useAccessibilityTTS();

    const isFullHeight = Array.isArray(buildingCategories) && buildingCategories.length < 10;

    const generateIsFullWidth = useMemo(() => {
        if (isFullscreen) {
            return false;
        }
        return Array.isArray(buildingCategories) && buildingCategories.length < 10;
    }, [buildingCategories, isFullscreen]);

    const generateIsFullHeight = useMemo(() => {
        if (!isFullscreen) {
            return false;
        }
        return Array.isArray(buildingCategories) && buildingCategories.length < 10;
    }, [buildingCategories, isFullscreen]);

    return (
        <section className={clsx(styles['vertical-category-wrapper'])}>
            <div
                className={clsx(styles['vertical-category__list'], 'scrollbar-hide')}
                data-region-nav={isShowCategoryList}
                data-region-order={4}
                data-tts-scope="floor-button"
            >
                {Array.isArray(buildingCategories) &&
                    buildingCategories?.length > 0 &&
                    buildingCategories.map((item, index) => (
                        <div
                            className={clsx({
                                'shrink-0': !isFullHeight,
                                'flex-1': isFullHeight,
                            })}
                            key={`${item.clientCode}-${index}`}
                        >
                            <CategoryButton
                                fullWidth={generateIsFullWidth}
                                fullHeight={generateIsFullHeight}
                                icon={getCategoryIcon(item.clientCode)}
                                onClick={() => onSelectCategory?.(item)}
                                tabIndex={isShowCategoryList ? 4 : -1}
                                data-interactive
                                data-playsound="ting"
                                onKeyUp={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                                    if (NAV_KEYS.includes(e.key)) {
                                        speak(
                                            t('floor_guide.text_to_speech.category_btn', {
                                                categoryName: item?.name?.[language] || item?.name?.['ko'] || '',
                                            })
                                        );
                                    }
                                }}
                            >
                                {item?.name?.[language] || item?.name?.['ko']}
                            </CategoryButton>
                        </div>
                    ))}
            </div>
            <div className={styles['vertical-category__view-store-btn']}>
                <ViewStoreButton
                    data-region-nav={isShowCategoryList}
                    data-region-order={isShowCategoryList ? 5 : -1}
                    icon={<PaperIcon />}
                    onClick={onShowPoiList}
                    tabIndex={isShowCategoryList ? 5 : -1}
                    data-interactive
                    data-playsound="ting"
                    aria-label={t('floor_guide.button.view_store_list')}
                    data-role={t('common.button.tts.popup_button')}
                >
                    {t('floor_guide.button.view_store_list')}
                </ViewStoreButton>
            </div>
        </section>
    );
};
